/* eslint-disable react/static-property-placement */
/* eslint-disable class-methods-use-this */
/* eslint-disable prefer-destructuring */
/* eslint-disable react/destructuring-assignment */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Big } from "big.js";
import cx from "classnames";
import { Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import GroupActions from "@/actions/GroupActions";
import ImageActions from "@/actions/ImageActions";
import ImageCropOverlay from "@/components/stage/Overlays/ImageCropOverlay.jsx";
import DataTypes, { BUTTON } from "@/constants/DataTypes";
import PROPERTY_MAPS from "@/constants/PropertyMaps";
import GroupStore from "@/stores/GroupStore";
import ImageStore from "@/stores/ImageStore";
import TransformerStore from "@/stores/TransformerStore";
import { handleStageContextMenu } from "@/utils/ContextMenuUtils";
import ElementUtils from "@/utils/ElementUtils";
import {
  getContainerData,
  positionElementToContainer,
} from "@/utils/GroupUtils";
import { isModifierKeyPressed } from "@/utils/HTMLUtils";
import SiteActions from "../../actions/SiteActions";
import TransformActions from "../../actions/TransformActions";
import UIActions from "../../actions/UIActions";
import DocumentStore from "../../document/DocumentStore";
import { COMMANDS } from "../../document/commands";
import AppConstants from "../../constants/AppConstants";
import { isIOS } from "../../constants/BrowserConstants";
import { LINE_THICKNESS } from "../../constants/MergeConstants";
import {
  CROP_MODE,
  DEFAULT_MODE,
  GROUP_FOCUS_MODE,
  SCROLL_ZOOM_AMOUNT_TRACKPAD,
  ZOOM_INCREMENT,
} from "../../constants/UIConstants";
import AppStore from "../../stores/AppStore";
import DesignViewStore from "../../stores/DesignViewStore";
import RightClickMenuStore from "../../stores/RightClickMenuStore";
import SelectionStore from "../../stores/SelectionStore";
import BlockUtils from "../../utils/BlockUtils";
import DesignContextUtils from "../../utils/DesignContextUtils";
import EventUtils from "../../utils/EventUtils";
import IDUtils from "../../utils/IDUtils";
import { gatherProps, getProp } from "../../utils/Merge/MergeUtils.mjs";
import { GetSelectedCanvasSnapPoints } from "../../utils/Snapping/SnappingUtils";
import {
  getElementsRightExtent,
  getStagePosition,
  toDesignPoint,
  toScreenSpace,
  toScrollPosition,
  toZoomedSpace,
} from "../../utils/ZoomUtils";
import BlockUpdateVO from "../../vo/BlockUpdate";
import TextEditor from "../editor/TextEditor";
import MountFrame from "../MountFrame";
import connectToStores from "../providers/connectToStores";
import Ruler from "../Ruler";
import SelectionLayer from "../SelectionLayer";
import StageFrameContent from "../StageFrameContent";
import StageOverlay from "../StageOverlay";
import ElementToolbar from "../stage/ElementToolbar";
import StageGestures from "../stage/StageGestures.mjs";
import FloatingStageItem from "../stage/FloatingStageItem";
import Guides from "../stage/Guides";
import GroupFocusOverlay from "../stage/Overlays/GroupFocusOverlay";
import OutsideStageDisableOverlay from "../stage/Overlays/OutsideStageDisableOverlay";
import TemporaryCropImage from "../stage/TemporaryElements/TemporaryCropImage";
import Transformer from "../transformer";
import MediaSelectHandler from "../utils/MediaSelectHandler";
import When from "../utils/When";
import NavigationOverlay from "./NavigationOverlay";

import styles from "./StageView.module.css";
import { getHandleMode } from "./StageView.utils";

const queryClient = new QueryClient();

/**
 * @typedef {Object} StagePositionProps
 * @property {number|Big} offsetLeft - The left offset of the stage in screen space.
 * @property {number|Big} offsetTop - The top offset of the stage in screen space.
 * @property {number|Big} designTargetLeft - Left of a position in design space, from the design canvas's left edge.
 * @property {number|Big} designTargetTop - Top of a position in design space, from the scroll content's top.
 */

export class StageView extends React.Component {
  static propTypes = {
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  static defaultProps = {
    width: "100%",
  };

  constructor(props) {
    super(props);
    this.state = {
      blockHeight: 0,
      resizing: false,
      snapGuides: { x: [], y: [] },
      snapPoints: { x: [], y: [] },
    };
    this.pageOffsetX = 0;
    this._pageOffsetRAF = 0;
    this.unsubscribers = [];

    this.stageScrollerRef = React.createRef();
    this.rulerHorizontalRef = React.createRef();
    this.rulerVerticalRef = React.createRef();
    this.transformerRef = React.createRef();
    // todo: remove difference and maybe lower time once we figure out race condition
    this._scrollZoom = _.throttle(this._scrollZoom.bind(this), 40);
    this.stageViewRef = React.createRef();
    this.pendingScrollProps = false;
  }

  componentDidMount() {
    const stageScroller = this.stageScrollerRef.current;
    stageScroller.addEventListener("scroll", this.handleScroll);
    stageScroller.addEventListener("wheel", this._handleWheel, {
      passive: false,
      capture: true,
    });

    // also covers browser zoom case
    window.addEventListener("resize", this._schedulePageOffsetSync);

    // old Safari (<= 17) fires no window resize on browser page zoom, but
    // the scroller's CSS-px box still reflows — without this the measured
    // --page-offset-x goes stale and the iframe content drifts off the
    // rulers until the next React update
    const scrollerResizeObserver = new ResizeObserver(
      this._schedulePageOffsetSync,
    );
    scrollerResizeObserver.observe(stageScroller);

    const stageGestures = new StageGestures({
      stage: this.stageViewRef.current,
      isTransforming: this._isTransforming,
      // crop mode blocks scrolling by design
      canPan: () => this._getMode() !== CROP_MODE,
      getViewport: this._getViewport,
      onStageGesture: this._applyStageGesture,
      onSwipe: this._onSwipe,
    });
    stageGestures.attach();

    this.unsubscribers = [
      TransformActions.ready.listen(this._transformReady),
      UIActions.scrollIntoView.listen(this._onScrollIntoView),
      UIActions.showLeftPanelView.listen(this._schedulePageOffsetSync),
      UIActions.showRightPanelView.listen(this._schedulePageOffsetSync),
      () => window.removeEventListener("resize", this._schedulePageOffsetSync),
      () => scrollerResizeObserver.disconnect(),
      () => stageGestures.detach(),
    ];

    this._schedulePageOffsetSync();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const layout = nextProps.layout; // mobile/desktop
    const selectedBlock = nextProps.selectedBlock;

    if (selectedBlock != null) {
      let baseBlock = selectedBlock;
      if (BlockUtils.hasSiteBlock(selectedBlock)) {
        baseBlock = selectedBlock.get(AppConstants.BLOCK_PROP_PARENT_BLOCK);
      }
      const blockHeight = baseBlock.getIn([layout, "h"], 0);
      this.setState({ blockHeight });
    }
  }

  componentDidUpdate(prevProps) {
    // delay scrollZoom reposition until zoom change is drilled. Every zoom that
    // renders while one is pending is positioned, not just the first: when a
    // render takes longer than a frame, a newer zoom is still in flight, and
    // painting it at the old scroll flicks the stage back a step.
    if (this.pendingScrollProps) {
      if (this.props.zoom !== prevProps.zoom) {
        this._positionStage(this.pendingScrollProps);
      }
      if (this.props.zoom === this._getRequestedZoom()) {
        this.pendingScrollProps = null;
      }
    }

    this._schedulePageOffsetSync();
  }

  // Coalesces multiple `componentDidUpdate` ticks into one rect read + write.
  _schedulePageOffsetSync = () => {
    if (this._pageOffsetRAF) return;
    this._pageOffsetRAF = requestAnimationFrame(() => {
      this._pageOffsetRAF = 0;
      this._syncPageOffsetVar();
      this._syncScrollbarVar();
    });
  };

  /**
   * Exposes the scroller's classic-scrollbar width (0 with overlay
   * scrollbars) so the horizontal ruler can shrink to the scroller's
   * clientWidth — its container-query centering is otherwise off by half
   * the scrollbar.
   */
  _syncScrollbarVar = () => {
    const stageScroller = this.stageScrollerRef.current;
    const stageView = this.stageViewRef.current;
    if (!stageScroller || !stageView) return;

    const scrollbarWidth = stageScroller.offsetWidth - stageScroller.clientWidth;
    if (scrollbarWidth === this._scrollbarWidth) return;
    this._scrollbarWidth = scrollbarWidth;

    stageView.style.setProperty("--stage-scrollbar-width", `${scrollbarWidth}px`);
  };

  /**
   * Sets pageOffset to shift stage frame and layers to meet
   */
  _syncPageOffsetVar = () => {
    const { layout, zoom } = this.props;
    if (!zoom) return;
    const canvasRect = document
      .getElementById(`${layout}_stage_canvas`)
      ?.getBoundingClientRect();
    const iframe = document.getElementById(`${layout}_frame`);
    const iframeRect = iframe?.getBoundingClientRect();
    if (!canvasRect || !iframeRect) return;

    // both rects are visual px: the canvas sits inside the transform-scaled
    // overlay and the iframe is transform-scaled itself
    const rawDiff = canvasRect.left - iframeRect.left;
    const pageOffsetX = toZoomedSpace(rawDiff, zoom);

    // write both targets or neither: a partial write desyncs the iframe
    // content from the outer layers, and the dedupe below would latch the
    // missed half in place until the offset next changes
    const iframeDoc = iframe.contentDocument;
    const stageView = this.stageViewRef.current;
    if (!iframeDoc || !stageView) return;

    if (pageOffsetX === this.pageOffsetX) return;
    this.pageOffsetX = pageOffsetX;

    const cssValue = `${pageOffsetX}px`;
    iframeDoc.documentElement.style.setProperty("--page-offset-x", cssValue);
    stageView.style.setProperty("--page-offset-x", cssValue);
  };

  componentWillUnmount() {
    if (this._pageOffsetRAF) {
      cancelAnimationFrame(this._pageOffsetRAF);
      this._pageOffsetRAF = 0;
    }

    this.unsubscribers.forEach((unsubscribe) => unsubscribe());

    const stageScroller = this.stageScrollerRef.current;
    stageScroller.removeEventListener("scroll", this.handleScroll);
    stageScroller.removeEventListener("wheel", this._handleWheel, {
      capture: true,
    });
  }

  handleScroll = () => {
    const stageScroller = this.stageScrollerRef.current;

    const rulerVertical = this.rulerVerticalRef.current.firstChild;
    rulerVertical.style.marginTop = `${-stageScroller.scrollTop}px`;

    this.rulerHorizontalRef.current?.style.setProperty(
      "--scroll-left",
      `${stageScroller.scrollLeft}px`,
    );
  };

  /**
   * Prevents wheel events from propagating, and calls _scrollZoom if the right key is held.
   * On iOS the scroller doesn't scroll natively (see `.jsScrolled`), so a
   * trackpad or mouse wheel scrolls it here, and a trackpad pinch, which
   * arrives as a wheel with ctrlKey, zooms.
   * @param {WheelEvent} e
   */
  _handleWheel = (e) => {
    if (isModifierKeyPressed(e) || (isIOS && e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      this._scrollZoom(e);
    } else if (isIOS) {
      e.preventDefault();
      this.stageScrollerRef.current.scrollBy(e.deltaX, e.deltaY);
    }
  };

  /**
   * Triggers increment zoom and prepares data to respond once zoom propagates back through from store state.
   * @param {WheelEvent} e
   */
  _scrollZoom = (e) => {
    const { layout } = this.props;

    const isTrackpad = Math.abs(e.deltaY) < 10;
    let zoomAmount = isTrackpad ? SCROLL_ZOOM_AMOUNT_TRACKPAD : ZOOM_INCREMENT;

    zoomAmount = e.deltaY > 0 ? -zoomAmount : zoomAmount;

    // keeps the design point under the mouse there through the new zoom
    const viewport = this._getViewport();
    this.pendingScrollProps = getStagePosition(toDesignPoint(e, viewport), e, viewport);

    UIActions.incrementZoom(zoomAmount, layout);
  };

  /**
   * Positions stage view scroll to the designTarget top/left. The offsets push that point from
   * the top left corner of the stage view container, and that offset is adjusted to the current zoom.
   * @param {StagePositionProps} position
   */
  _positionStage(position) {
    const { x, y } = toScrollPosition(position, this.props.zoom, this._getCanvasLeft());
    this.stageScrollerRef.current.scroll(x, y);
  }

  /**
   * Scroller geometry at the start of a two-finger gesture.
   * @returns {import("@/utils/ZoomUtils").Viewport}
   */
  _getViewport = () => {
    const stageScroller = this.stageScrollerRef.current;
    const { left, top } = stageScroller.getBoundingClientRect();
    return {
      left,
      top,
      scrollLeft: stageScroller.scrollLeft,
      scrollTop: stageScroller.scrollTop,
      canvasLeft: this._getCanvasLeft(left),
      zoom: this.props.zoom,
    };
  };

  /**
   * Screen px from the scroll content's left edge to the design canvas. The
   * canvas is centered, so this is its margin while the design is narrower
   * than the stage, and 0 once it fills it.
   * @param {number=} scrollerLeft - The scroller's client left, when already measured.
   * @returns {number}
   */
  _getCanvasLeft = (scrollerLeft) => {
    const canvas = document.getElementById(`${this.props.layout}_stage_canvas`);
    if (!canvas) return 0;

    const stageScroller = this.stageScrollerRef.current;
    return (
      canvas.getBoundingClientRect().left -
      (scrollerLeft ?? stageScroller.getBoundingClientRect().left) +
      stageScroller.scrollLeft
    );
  };

  /**
   * The zoom last requested for this layout: the rendered one, or one still
   * on its way to render.
   * @returns {number}
   */
  _getRequestedZoom = () =>
    DesignViewStore.getState().getIn(["stage", this.props.layout, "zoom"], 1);

  /**
   * Applies a two-finger pan/zoom frame. A zoom change goes through the same
   * pendingScrollProps path as Meta+wheel zoom, so the scroll is positioned
   * when the new zoom renders. The position is a design point and a screen
   * offset, so it holds for whichever zoom renders next.
   * @param {StagePositionProps & { zoom: number }} gesture
   */
  _applyStageGesture = ({ zoom: nextZoom, ...position }) => {
    const { layout, zoom } = this.props;
    const requested = this._getRequestedZoom();
    this.pendingScrollProps = position;
    if (nextZoom === zoom) {
      this._positionStage(position);
      // nothing else in flight: no render is coming to apply it
      if (requested === zoom) this.pendingScrollProps = null;
    }
    if (nextZoom !== requested) UIActions.setZoom(nextZoom, layout);
  };

  /**
   * Three fingers swiped across the stage: left undoes, right redoes.
   * @param {import("../stage/StageGestures.mjs").SwipeDirection} direction
   */
  _onSwipe = (direction) => {
    if (direction === "left") SiteActions.undo();
    else if (direction === "right") SiteActions.redo();
  };

  _onTransformStart = (data) => {
    const actions = this._getActions();

    actions.startTransform(data);

    this._getMode() === DEFAULT_MODE &&
      this.setState((prevState, props) => ({
        snapPoints: GetSelectedCanvasSnapPoints({
          blockHeight: prevState.blockHeight,
          layout: props.layout,
          selectedBlock: props.selectedBlock,
          viewState: props.viewState,
        }),
      }));
  };

  _onTransform = (data) => {
    const actions = this._getActions();
    actions.transform(data);
  };

  _onTransformEnd = (data) => {
    const actions = this._getActions();
    actions.endTransform(data);
  };

  _transformReady = (data) => {
    const transformer = this.transformerRef.current;
    if (transformer && this.props.layout === data.layout) {
      transformer.redraw();
    }
  };

  // todo: probably update for inner group stuff?
  _setSnappingGuides = ({ snapGuides, reset = false }) => {
    if (!reset && snapGuides !== undefined) {
      this.setState({ snapGuides });
    } else if (reset || snapGuides === undefined) {
      this.setState({ snapGuides: { x: [], y: [] } });
    }
  };

  _onTransformerDoubleClick = (e) => {
    const mode = this._getMode();

    if (mode === CROP_MODE) return;

    const { layout, selectedBlock } = this.props;

    const selectedElems = selectedBlock.getIn(["selected", "elements"]);
    if (selectedElems && selectedElems.size === 1) {
      const blockId = selectedBlock.get("id");
      const element = selectedElems.first();
      const elemType = element.type;
      const elemId = element.id;
      let editConfig = null;

      const selElement = BlockUtils.getBlockElement(selectedBlock, elemId);
      const url = selElement.getIn(["content", "url"], false);

      switch (elemType) {
        case AppConstants.ELEMENT_TYPE_TEXT:
          editConfig = {
            id: elemId,
            blockId,
            altEdit: e.altKey,
          };

          break;
        case AppConstants.ELEMENT_TYPE_BUTTON:
          editConfig = {
            id: elemId,
            type: AppConstants.ELEMENT_TYPE_BUTTON,
            blockId,
            altEdit: e.altKey,
          };

          break;
        case AppConstants.ELEMENT_TYPE_HTML_TEXT:
          editConfig = {
            id: elemId,
            blockId,
            altEdit: true,
          };

          break;
        case AppConstants.ELEMENT_TYPE_IFRAME:
          if (_.isEmpty(url)) {
            editConfig = {
              id: elemId,
              blockId,
              altEdit: true,
            };
          }
          break;
        case AppConstants.ELEMENT_TYPE_GRAPHIC:
        case AppConstants.ELEMENT_TYPE_SVG:
          MediaSelectHandler.selectMedia(elemId, selElement, { type: "image" });
          break;
        case AppConstants.ELEMENT_TYPE_GALLERY:
          MediaSelectHandler.selectMedia(elemId, selElement, {
            type: "images",
          });
          break;
        case DataTypes.GROUP:
          GroupActions.focusGroup({
            groupSelectionId: elemId,
            selectedBlock,
            layout,
            focusCoordinates: {
              x: e.clientX,
              y: e.clientY,
            },
          });
          break;
        default:
          break;
      }

      if (editConfig) {
        UIActions.editTextElement(editConfig);
      }
    }
  };

  _onDuplicateDrag = () => {
    if (this._getMode() !== DEFAULT_MODE) return;

    SiteActions.copy();
    DocumentStore.dispatch({ type: COMMANDS.PASTE, payload: { select: false } });
  };

  _onBlockResizeComplete = (blockHeight) => {
    const blockId = this.props.selectedBlock.get("id");
    const layout = this.props.layout;

    const update = new BlockUpdateVO();

    update.add(blockId, [layout, "h"], blockHeight);

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });

    this.setState({ resizing: false });
  };

  _onBlockResizeHeight = (blockHeight) => {
    this.setState({ blockHeight });
  };

  _onBlockResizeStart = () => {
    this.setState({ resizing: true });
  };

  _onElementRightClick = (stageViewRef) => {
    if (this._getMode() === CROP_MODE) {
      return (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
    }
    return handleStageContextMenu(
      stageViewRef,
      this.props.selectedBlock,
      this.props.layout,
      this.props.zoom,
    );
  };

  _unlockElement = (elemId) => {
    const update = new BlockUpdateVO();
    update.add(elemId, AppConstants.LOCK_EDITING_KEY, null);
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  _onScrollIntoView = (data) => {
    let domId = null;
    const { layout } = this.props;
    if (data.type === "block") {
      domId = IDUtils.getDOMID("si_block", layout, data.id);
    }

    if (domId) {
      const elem = document.getElementById(domId);
      const stageScroller = this.stageScrollerRef.current;
      // in screenSpace
      const elementTop =
        elem?.getBoundingClientRect()?.top -
        stageScroller.getBoundingClientRect()?.top;

      if (elementTop) {
        // both values are screen px — scroll positions live in the scaled space
        const scrollTop = stageScroller.scrollTop + elementTop;
        stageScroller.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }
  };

  /**
   * Determine if there is a selectable item begin that is being clicked on behind the transformer
   */
  _onTransformerClick = (e) => {
    // prevent reselection
    if (this._getMode() === CROP_MODE) return;

    const elem = document.elementFromPoint(e.clientX, e.clientY);

    const selectedBlockId = this.props.selectedBlock.get("id");
    const blockId = elem.dataset?.blockId;

    // prevents an error when elem from deselected canvas
    // overlaps elem from selected canvas
    if (blockId && selectedBlockId !== blockId) {
      UIActions.selectBlock({
        id: blockId,
        layout: this.props.layout,
      });

      return true;
    }

    if (
      elem.classList.contains("selectable") &&
      !elem.classList.contains("selected")
    ) {
      const elemId = elem?.dataset?.id;
      const elemType = elem?.dataset?.type;
      const selection = EventUtils.selectionType(e);

      UIActions.selectElement({
        id: elemId,
        type: elemType,
        layout: this.props.layout,
        blockId,
        addTo: selection.multi,
        from: "stage",
      });

      return true;
    }

    return false;
  };

  _getElementDomId = (elementId) =>
    `#si_element_${this.props.layout}_${IDUtils.getDOMID(elementId)}`;

  _getTransformer = ({ topOffset, selectedBlockStart, designContext }) => {
    const { layout, theme, selectedBlock, zoom } = this.props;
    const mode = this._getMode();

    const transformerStyle = {
      position: "absolute",
      height: "100%",
      width: "100%",
      zIndex: 2,
    };

    let plugin = null;

    const selectedElems = selectedBlock.getIn(["selected", "elements"]);
    const isSingleElement = selectedElems && selectedElems.size === 1;
    const isSingleGroup =
      isSingleElement && selectedElems.first().type === DataTypes.GROUP;
    const isSelectionContained =
      selectedElems && selectedElems.every((elem) => elem.containedBy);
    const enabled = this.props.hasSelection;

    if (isSingleElement) {
      const selElement = selectedElems.first();
      const selElemType = selElement.type;
      const selElemId = selElement.id;
      const isButton = selElemType === AppConstants.ELEMENT_TYPE_BUTTON;

      const isGroupSelectedAndFocused =
        mode === GROUP_FOCUS_MODE &&
        selElemId === this.props.groupState.get("groupId");
      const isGroupElementSelectedAndUnfocused =
        mode === DEFAULT_MODE && isSelectionContained;

      // deselect group on focus so the transformer doesn't cover the elements, or if a selection is contained but not focused.
      if (isGroupSelectedAndFocused || isGroupElementSelectedAndUnfocused)
        return null;

      if (selElement.edit) {
        const element = BlockUtils.getBlockElement(selectedBlock, selElemId);
        const enableToolbar = !element.hasIn(["wp", "field"]) && !isButton;

        switch (selElemType) {
          case AppConstants.ELEMENT_TYPE_HTML_TEXT:
          case AppConstants.ELEMENT_TYPE_TEXT:
            plugin = (
              <TextEditor
                mode={selElement.editor}
                theme={theme}
                layout={layout}
                elementId={selElemId}
                element={element}
                enableToolbar={enableToolbar}
                includeSample
                designContext={designContext}
              />
            );
            break;

          case AppConstants.ELEMENT_TYPE_BUTTON:
            plugin = (
              <TextEditor
                mode={selElement.editor}
                theme={theme}
                layout={layout}
                elementId={selElemId}
                element={element}
                parentId={selElement.buttonId}
                buttonElement={element}
                enableToolbar={enableToolbar}
                includeSample
                designContext={designContext}
              />
            );
            break;

          case AppConstants.ELEMENT_TYPE_IFRAME:
            plugin = (
              <TextEditor
                mode={selElement.editor}
                theme={theme}
                layout={layout}
                elementId={selElemId}
                element={element}
                contentPath="code"
                popupTitle="Edit Code"
                designContext={designContext}
              />
            );

            break;
          default:
            break;
        }
      }
    }

    const handleMode = getHandleMode(mode, selectedBlock);

    let selectedElements = SelectionStore.getFullVisibleElementsJS(layout);
    if (!selectedElements.length) return null;

    // needing to copy height value to mobile for line with merged thickness
    if (layout === AppConstants.LAYOUT_MOBILE) {
      selectedElements = selectedElements.map((elem) => ({
        ...elem,
        mobile: { ...elem.mobile, h: getProp(elem, layout, LINE_THICKNESS) },
      }));
    }

    const baseElement = selectedElements[0];

    if (baseElement?.type === BUTTON) {
      const themeObject = theme?.toJS?.() ?? {};
      const minHeight = ElementUtils.getMinHeight(
        baseElement,
        layout,
        themeObject,
      );
      const minWidth = ElementUtils.getMinWidth(
        baseElement,
        layout,
        themeObject,
      );
      baseElement[layout].minHeight = minHeight;
      baseElement[layout].minWidth = minWidth;

      // apply to base element for initial bounds
      if (baseElement[layout].h < minHeight) baseElement[layout].h = minHeight;
      if (baseElement[layout].w < minWidth) baseElement[layout].w = minWidth;
    }

    let transformElements = selectedElements;
    let constrainArea;

    if (mode === CROP_MODE) {
      const selectedElement = ImageStore.getTransformerData()?.toJS();
      transformElements = [selectedElement];

      const around = {
        x: baseElement[layout].x,
        y: baseElement[layout].y,
        w: baseElement[layout].w,
        h: baseElement[layout].h,
        a: baseElement[layout].a,
      };

      // shift the "around" bounds into the container the crop tool was positioned in
      const container = ImageStore.getContainer();
      if (container) {
        Object.assign(
          around,
          positionElementToContainer(around, container.get(layout).toJS(), true),
        );
      }

      constrainArea = { around };
    }

    let containedArea;

    // offset inner elements by parents position
    if (mode === GROUP_FOCUS_MODE) {
      const groupSelectionId = GroupStore.getGroupSelectionId();

      if (
        (selectedElements.length === 1 &&
          // todo: figure out why selected child element has group as id
          baseElement?.id === GroupStore.getGroupId() &&
          baseElement.type === DataTypes.GROUP) ||
        // indicates the mode hasn't changed yet but group is blurred
        !groupSelectionId
      )
        return;

      const focusedGroup = BlockUtils.getBlockElement(
        selectedBlock,
        groupSelectionId,
      );

      if (
        !focusedGroup ||
        !BlockUtils.isVisibleInLayout(focusedGroup, layout)
      ) {
        // clear selection and group focus if parent is not visible.
        // Happens when parent is hidden while child is selected.
        UIActions.clearSelectedElements();
        return;
      }

      selectedElements = selectedElements.map((elem) => {
        // account for possibly having outside things selected through layers panel
        if (elem?.pId) {
          const layoutProps = elem[layout];
          const parent = BlockUtils.getChildParent(selectedBlock, elem);

          if (!containedArea) {
            const parentPosition = gatherProps(
              parent,
              layout,
              PROPERTY_MAPS.ELEMENT_POSITION,
              false,
            )?.toJS();

            // Never-rotated containers store no angle
            if (parentPosition)
              containedArea = { ...parentPosition, a: parentPosition.a ?? 0 };
          }

          // needs some sort of mat rotation applied from parent
          elem[layout] = {
            ...layoutProps,
            ...positionElementToContainer(layoutProps, containedArea, true),
          };
        }

        return elem;
      });
    } else if (selectedElements.every((element) => !!element.pId)) {
      const firstSelectedPath = selectedElements[0].selected.id;
      const containerElement = getContainerData(
        selectedBlock,
        firstSelectedPath,
      );
      // return early when contained element selected and container is hidden
      if (
        containerElement &&
        !BlockUtils.isVisibleInLayout(containerElement, layout)
      )
        return;
    }

    // aspect ratio to constrain the transformer to
    const aspectRatio =
      mode === CROP_MODE
        ? ImageStore.getAspectRatio()
        : isSingleGroup
          ? new Big(baseElement[layout].w).div(baseElement[layout].h).toNumber()
          : null;

    return (
      <Transformer
        ref={this.transformerRef}
        key={`${layout}-transformer`}
        id={`${layout}-transformer`}
        style={transformerStyle}
        enabled={enabled}
        targetIds={this.props.selectedElementIds}
        getDomID={this._getElementDomId}
        idAttr="data-id"
        handleMode={handleMode}
        aspectRatio={aspectRatio}
        layout={layout}
        onClickBehind={this._onTransformerClick}
        onTransformStart={this._onTransformStart}
        onTransform={this._onTransform}
        unlockElement={this._unlockElement}
        onTransformEnd={this._onTransformEnd}
        onDoubleClick={this._onTransformerDoubleClick}
        onContextMenu={this._onElementRightClick(this.stageViewRef)}
        onDuplicateDrag={this._onDuplicateDrag}
        snapPoints={this.state.snapPoints}
        setSnappingGuides={this._setSnappingGuides}
        selectedBlockStart={selectedBlockStart}
        selectedElements={transformElements}
        plugin={plugin}
        zoom={zoom}
        scrollerRef={this.stageScrollerRef}
        topOffset={topOffset}
        currentBlockId={`si_block_${layout}_${selectedBlock.get("id")}`}
        store={this._getStore()}
        actions={this._getActions()}
        constrainArea={constrainArea}
        containedArea={containedArea}
      />
    );
  };

  _getMode() {
    return this.props.viewState.getIn(["mode", "type"]);
  }

  _getStore() {
    return this._getMode() === CROP_MODE ? ImageStore : TransformerStore;
  }

  /**
   * Whether the mode's transform is dragging: the crop's in crop mode, the
   * transformer's otherwise. A finger landing during it starts no stage gesture.
   * @returns {boolean}
   */
  _isTransforming = () => this._getStore().getTransformingStatus();

  _getActions() {
    return this._getMode() === CROP_MODE ? ImageActions : TransformActions;
  }

  _getDisableOverlay = () => {
    const { layout, selectedBlock } = this.props;
    const layoutIsActive =
      selectedBlock.getIn(["selected", "layout"]) === layout;
    const mode = this._getMode();

    return mode === CROP_MODE && layoutIsActive ? (
      <OutsideStageDisableOverlay onClick={ImageActions.exitAndSaveCropMode} />
    ) : null;
  };

  _getTemporaryElements = (designContext) => {
    const { layout } = this.props;
    const mode = this._getMode();

    return mode === CROP_MODE ? (
      <TemporaryCropImage layout={layout} designContext={designContext} />
    ) : null;
  };

  _getUpperInlineOverlay = () => {
    const { layout } = this.props;
    const mode = this._getMode();

    return mode === CROP_MODE ? (
      <ImageCropOverlay
        layout={layout}
        onBackgroundClick={() => ImageActions.exitAndSaveCropMode()}
      />
    ) : mode === GROUP_FOCUS_MODE ? (
      <GroupFocusOverlay layout={layout} />
    ) : null;
  };

  render() {
    const {
      blocks,
      layout,
      selectedBlock,
      theme,
      background,
      blockContainer,
      zoom = 1,
      buttonState,
      rightClickMenu,
      cropState,
      groupState,
    } = this.props;
    const usingSiteBlock = BlockUtils.isUsingSiteBlock(selectedBlock);
    const assetUrl = blockContainer.getIn(["config", "assetURL"]);
    const designContext = DesignContextUtils.createContext({ assetUrl });
    const mode = this._getMode();

    if (GroupStore.pendingSelection()) {
      GroupStore.collectPendingSelection();
    }

    const classCx = cx({
      "stage-frame-container": true,
      "stage-frame-container-rulers": true,
      [styles.stageScroller]: true,
      [styles.jsScrolled]: isIOS,
    });

    let frameHeight = 0;
    let layoutWidth = 0;
    let contentRight = 0;
    let selectedBlockStart = 0;
    const selectedBlockHeight = this.state.blockHeight;
    const resizing = this.state.resizing;

    let stageTopPad = AppConstants.STAGE_PADDING;
    let stageBottomPad = AppConstants.STAGE_PADDING;

    let topBlock = null;
    let bottomBlock = null;

    if (blocks != null) {
      blocks.forEach((block) => {
        if (BlockUtils.isVisibleInLayout(block, layout)) {
          if (topBlock == null) {
            topBlock = block;
          }
          bottomBlock = block;

          let baseBlock = block;
          if (BlockUtils.hasSiteBlock(block)) {
            baseBlock = block.get(AppConstants.BLOCK_PROP_PARENT_BLOCK);
          }

          if (selectedBlock.get("id") === block.get("id")) {
            selectedBlockStart = frameHeight;
          }

          frameHeight += baseBlock.getIn([layout, "h"], 0);
          if (baseBlock.getIn([layout, "w"]) > layoutWidth) {
            layoutWidth = baseBlock.getIn([layout, "w"]);
          }

          // elements hanging off the right create x-scrollable space via
          // their selection hit divs; track that extent so the focus-mode
          // shield can cover it (site-block instances render no hit divs)
          if (!BlockUtils.isUsingSiteBlock(block)) {
            const elementsRight = getElementsRightExtent(block, layout);
            if (elementsRight > contentRight) contentRight = elementsRight;
          }
        }
      });

      if (topBlock != null) {
        stageTopPad += BlockUtils.getBlockTopPadding(topBlock, layout);
      }

      if (bottomBlock != null) {
        stageBottomPad += BlockUtils.getBlockBottomPadding(bottomBlock, layout);
      }
    }

    selectedBlockStart += stageTopPad;

    frameHeight += stageTopPad + stageBottomPad;

    // If in crop mode, the visible stage must contain the selected uncropped image on canvas
    const CROP_FRAME_PADDING = 10; // Extra padding to account for crop handles
    if (mode === CROP_MODE && cropState?.get("selectedImageId")) {
      const {
        x: cropX = 0,
        y: cropY = 0,
        w: cropWidth = 0,
        h: cropHeight = 0,
      } = ImageStore.getCurrentCrop(layout)?.toJS?.() ?? {};
      frameHeight = Math.max(
        frameHeight,
        selectedBlockStart + cropY + cropHeight + CROP_FRAME_PADDING,
      );
      contentRight = Math.max(
        contentRight,
        cropX + cropWidth + CROP_FRAME_PADDING,
      );
    }

    // resizes iframe to account for scaling
    const visualMinWidth = toScreenSpace(layoutWidth, zoom);
    const visualHeight =
      toScreenSpace(frameHeight, zoom) + stageTopPad * (1 - zoom);

    const style = {
      "--page-offset-y": `${selectedBlockStart}px`,
      "--stage-layout-width": `${layoutWidth}px`,
      "--stage-content-right": `${Math.ceil(contentRight)}px`,
    };

    const frameStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      minWidth: visualMinWidth,
      height: visualHeight,
    };

    const pageStyle = {
      width: layoutWidth,
      paddingTop: stageTopPad,
      paddingBottom: stageBottomPad,
    };

    let activeClass = "";
    let transformer = null;

    if (this.props.hasSelection) {
      activeClass = "active";
      transformer = this._getTransformer({
        topOffset: stageTopPad,
        selectedBlockStart,
        designContext,
      });
    }

    let overlayHelperVerticalDt = null;
    let overlayHelperVerticalMob = null;
    let overlayHelperHorizontal = null;
    let resizeIndicator = null;

    const mobileOverlay = this.props.viewState.getIn([
      "stage",
      "overlays",
      AppConstants.STAGE_OVERLAY_MOBILE,
    ]);
    const guides = this.props.viewState.getIn([
      "stage",
      "overlays",
      AppConstants.STAGE_OVERLAY_GUIDES,
    ]);

    if (mobileOverlay && layout === AppConstants.LAYOUT_MOBILE) {
      // mobile phone overlay 👇
      overlayHelperVerticalMob = (
        <div className="overlay-helper-vertical-mobile" />
      );
    }

    const disableOverlay = this._getDisableOverlay();
    const temporaryElements = this._getTemporaryElements(designContext);

    if (guides === 1) {
      overlayHelperVerticalDt = (
        <div className="overlay-helper-vertical-desktop" />
      );

      if (selectedBlockHeight > 0) {
        // visual px: these render unscaled in the scroller, so an unscaled
        // layoutWidth would grow scrollWidth past the scaled frame
        const overlayHorizontalTopStyle = {
          top: `${toScreenSpace(selectedBlockStart, zoom) - 1}px`,
          minWidth: toScreenSpace(layoutWidth, zoom),
        };
        const overlayHorizontalBottomStyle = {
          top: `${toScreenSpace(selectedBlockStart + selectedBlockHeight, zoom)}px`,
          minWidth: toScreenSpace(layoutWidth, zoom),
        };

        overlayHelperHorizontal = (
          <div className="overlay-helper-horizontal">
            <div className="block-top" style={overlayHorizontalTopStyle} />
            <div
              className="block-bottom"
              style={overlayHorizontalBottomStyle}
            />
          </div>
        );
      }
    }

    if (resizing) {
      const dynamicStyle = {
        top: `${toScreenSpace(selectedBlockStart + selectedBlockHeight, zoom) - 12}px`,
      };

      resizeIndicator = (
        <div className={styles.resizeIndicatorStyle} style={dynamicStyle}>
          {selectedBlockHeight}
        </div>
      );
    }

    const modeProps = {
      type: mode,
      crop: {
        selectedImageId: cropState.get("selectedImageId"),
      },
      group: {
        id: groupState.get("groupId"),
        selectionId: groupState.get("groupSelectionId"),
        // selectedBlock
      },
    };

    const stageFrameProps = {
      background,
      designContext,
      theme,
      blocks,
      layout,
      layoutWidth,
      pageStyle,
      buttonState,
      mode: modeProps,
    };

    const _handleStageContextMenu = handleStageContextMenu(
      this.stageViewRef,
      selectedBlock,
      layout,
      zoom,
    );

    const toolbarActive =
      selectedBlock.getIn(["selected", "layout"]) === layout &&
      selectedBlock.getIn(["selected", "elements"], Map()).size;

    return (
      <div
        id={`${layout}_stage`}
        data-layout={layout}
        data-testid={`stage-view-${layout}`}
        style={style}
        className={cx(
          styles["stage-view"],
          "stage-view",
          activeClass,
          styles[layout],
        )}
        data-intercom-target={`${layout} stage`}
        ref={this.stageViewRef}
      >
        <NavigationOverlay scrollerRef={this.stageScrollerRef} />
        <Ruler
          direction="horizontal"
          domRef={this.rulerHorizontalRef}
          width={layoutWidth}
          zoom={zoom}
        />
        <Ruler
          direction="vertical"
          domRef={this.rulerVerticalRef}
          height={frameHeight}
          selectionStart={selectedBlockStart}
          selectionSize={selectedBlockHeight}
          resizeEnabled={!usingSiteBlock}
          onResizeComplete={this._onBlockResizeComplete}
          onResize={this._onBlockResizeHeight}
          onResizeStart={this._onBlockResizeStart}
          zoom={zoom}
        />

        <div
          style={{
            "--zoom": zoom,
            "--stage-padding-top": stageTopPad,
          }}
          className={classCx}
          ref={this.stageScrollerRef}
        >
          <MountFrame
            key={`${layout}-frame`}
            id={`${layout}_frame`}
            style={frameStyle}
            mountStyle={{
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
          >
            <QueryClientProvider client={queryClient}>
              <StageFrameContent {...stageFrameProps} />
            </QueryClientProvider>
          </MountFrame>
          {/* outside StageOverlay: scale() would turn this fixed viewport
              shield into an overlay-anchored box and grow scroll extents */}
          {disableOverlay}
          <When it={this.props.showSelectLayer}>
            <StageOverlay
              key={`${layout}-overlay`}
              height={frameHeight}
              layoutWidth={layoutWidth}
              zoom={zoom}
            >
              <SelectionLayer
                key={`${layout}-selection`}
                disabled={mode === CROP_MODE}
                selectedBlock={selectedBlock}
                blocks={blocks}
                blockContainer={blockContainer}
                layout={layout}
                layoutWidth={layoutWidth}
                mode={modeProps}
                pageStyle={pageStyle}
                rightClickMenu={rightClickMenu}
                handleStageContextMenu={_handleStageContextMenu}
                theme={theme}
                zoom={zoom}
              />
              {temporaryElements}
              {this._getUpperInlineOverlay()}
              {transformer}
              {overlayHelperVerticalDt}
              <Guides
                layoutWidth={layoutWidth}
                selectedBlockHeight={selectedBlockHeight}
                snapGuides={this.state.snapGuides}
                layout={this.props.layout}
                viewState={this.props.viewState}
              />
              {overlayHelperVerticalMob}
            </StageOverlay>
          </When>
          {overlayHelperHorizontal}
          {resizeIndicator}
          <FloatingStageItem
            active={toolbarActive}
            block={blockContainer.get("selectedBlock")}
            getTarget={mode === CROP_MODE}
            layout={this.props.layout}
            stageRef={this.stageScrollerRef.current}
            yOffset={selectedBlockStart}
            zoom={zoom}
          >
            <ElementToolbar blockContainer={blockContainer} layout={layout} />
          </FloatingStageItem>
        </div>
      </div>
    );
  }
}

export default connectToStores(StageView, [
  {
    store: AppStore,
    state: { buttonState: AppStore.getButtonState },
  },
  {
    store: RightClickMenuStore,
    state: { rightClickMenu: RightClickMenuStore.getRightClickMenuState },
  },
  {
    store: ImageStore,
    state: { cropState: ImageStore.getState },
  },
  {
    store: GroupStore,
    state: { groupState: GroupStore.getState },
  },
]);
